import { BadRequest, ServiceUnavailable } from 'common/errors'
import { checker, validator } from 'common/utils/validator'
import { useEffect, useState } from 'react'
import { isNotEmpty } from 'src/shared/utils/field-validators'
import { registerConfirm } from '../api/website-public-page-api'
import { PublicPageFieldSlugEnum } from '../enums/public-page-field-slug-enum'

type ErrorFields = Partial<Record<PublicPageFieldSlugEnum | 'root', string[]>>

const validateEmptyField = checker(
  validator('validation.not_empty', isNotEmpty),
)

export const useRegisterConfirmSubmit = (token: string) => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ErrorFields>()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [plainPassword, setPlainPassword] = useState('')
  const [confirmPlainPassword, setConfirmPlainPassword] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear field error when user types
  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.FirstName]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.FirstName]: undefined,
      }))
    }
  }, [firstName])

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear field error when user types
  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.LastName]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.LastName]: undefined,
      }))
    }
  }, [lastName])

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear field error when user types
  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.Password]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.Password]: undefined,
      }))
    }
  }, [plainPassword])

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear field error when user types
  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.ConfirmPassword]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.ConfirmPassword]: undefined,
      }))
    }
  }, [confirmPlainPassword])

  const handleSubmit = async (captchaToken?: string) => {
    const firstNameError = validateEmptyField(firstName)
    if (firstNameError?.length) {
      setErrors({ [PublicPageFieldSlugEnum.FirstName]: firstNameError })
      return
    }

    const lastNameError = validateEmptyField(lastName)
    if (lastNameError?.length) {
      setErrors({ [PublicPageFieldSlugEnum.LastName]: lastNameError })
      return
    }

    const passwordError = validateEmptyField(plainPassword)
    if (passwordError?.length) {
      setErrors({ [PublicPageFieldSlugEnum.Password]: passwordError })
      return
    }

    const confirmPasswordError = validateEmptyField(confirmPlainPassword)
    if (confirmPasswordError?.length) {
      setErrors({
        [PublicPageFieldSlugEnum.ConfirmPassword]: confirmPasswordError,
      })
      return
    }

    if (plainPassword !== confirmPlainPassword) {
      setErrors({
        [PublicPageFieldSlugEnum.ConfirmPassword]: [
          'validation.password.mismatch',
        ],
      })
      return
    }

    setErrors(undefined)

    try {
      setLoading(true)
      const { data } = await registerConfirm({
        token,
        plainPassword,
        firstName,
        lastName,
        captcha: captchaToken ?? '',
      })
      if (data?.redirect) {
        window.location.href = data.redirect
      }
    } catch (e) {
      if (e instanceof ServiceUnavailable) {
        setErrors({ root: ['core.errors.service_temporary_unavailable'] })
      } else if (e instanceof BadRequest) {
        const { fields, common } = e.response?.data?.errors || {}
        const newErrors: ErrorFields = {}
        if (fields) {
          Object.entries(fields).forEach(([key, messages]) => {
            const slug = key as PublicPageFieldSlugEnum
            if (
              slug === PublicPageFieldSlugEnum.FirstName ||
              slug === PublicPageFieldSlugEnum.LastName ||
              slug === PublicPageFieldSlugEnum.PlainPassword
            ) {
              newErrors[slug] = [messages?.join('\n')]
            }
          })
        }
        if (common) {
          newErrors.root = [common?.join('\n')]
        }
        setErrors(newErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    errors,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    plainPassword,
    setPlainPassword,
    confirmPlainPassword,
    setConfirmPlainPassword,
    handleSubmit,
  }
}
